import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IntegratedAccount } from './accounts';
import { Button, Select, Table, TableCellType } from '@shared/widgets';

interface IntegrationAccountTableProps {
  isLoading?: boolean;
  error?: string | null;
  accounts: IntegratedAccount[];
  categories: string[] | undefined;
  currentPage: number;
  totalItemCount: number;
  countItemPage: number;
  onChangeCategory: ({ accountId, category }: { accountId: number; category: string }) => void;
  onConnect: (row: IntegratedAccount) => void;
  onDisconnect: (row: IntegratedAccount) => void;
  onChangeCountItemPage: (count: number) => void;
  onChangePage: (index: number) => void;
  onRefresh?: () => void;
}

export const IntegrationAccountTable = (props: IntegrationAccountTableProps) => {
  const { t } = useTranslation('integration');

  const columns = useMemo(() => [t('table.columns.account'), t('table.columns.currency'), t('table.columns.category')], [t]);
  return (
    <Table
      isScrollable
      isLoading={props.isLoading}
      error={props.error}
      columns={columns}
      items={props.accounts.map((row) => {
        return [
          { type: TableCellType.Text, value: row.accountNumber },
          { type: TableCellType.Text, value: row.currency },
          {
            type: TableCellType.Custom,
            value: (
              <div className='w-fit'>
                <Select
                  className='min-w-[300px]'
                  items={props.categories || []}
                  disabled={row.integration?.connected}
                  nullValue={{ isEnable: false, text: t('table.placeholders.defineCategory') }}
                  value={row.editCategory || row.integration?.category}
                  onChange={(val) => props.onChangeCategory({ accountId: row.id, category: val! })}
                />
              </div>
            ),
          },
          {
            type: TableCellType.Custom,
            value: (
              <div className='flex justify-end'>
                {row.editCategory || (row.integration && !row.integration.connected) ? (
                  <Button className='min-w-32' color='primary' onClick={() => props.onConnect(row)}>
                    {t('table.buttons.connect')}
                  </Button>
                ) : row.integration?.category ? (
                  <Button className='min-w-32' color='outlined' onClick={() => props.onDisconnect(row)}>
                    {t('table.buttons.disconnect')}
                  </Button>
                ) : (
                  <Button className='min-w-32' color='primary' disabled>
                    {t('table.buttons.connect')}
                  </Button>
                )}
              </div>
            ),
          },
        ];
      })}
      pagination={{
        currentPage: props.currentPage,
        totalItemCount: props.totalItemCount,
        countItemPage: props.countItemPage,
        onChangePage: props.onChangePage,
        onChangeCountItemPage: props.onChangeCountItemPage,
      }}
      onRefresh={props.onRefresh}
    />
  );
};
